<template>
    <div class="mail-doc-preview">
        <div
            v-if="loading"
            class="mail-doc-preview__status"
            role="status"
            aria-live="polite"
        >
            <b-spinner small variant="secondary" />
            <span class="ml-1">갱신 중</span>
        </div>

        <div
            v-if="error"
            class="mail-doc-preview__error"
            role="alert"
        >
            {{ error }}
        </div>

        <div
            v-if="!template && !rendered"
            class="mail-doc-preview__empty text-muted small"
        >
            내용이 없습니다.
        </div>

        <!-- 첫 렌더 이후에는 같은 MailDocCanvas/iframe 인스턴스를 유지한다. -->
        <MailDocCanvas
            v-if="rendered"
            class="mail-doc-preview__canvas"
            :value="rendered"
            :values="values"
            preview
        />
    </div>
</template>

<script>
import snippetExpand from '../snippetExpand'
import MailDocCanvas from './MailDocCanvas.vue'

function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
}

export default {
    name: 'MailDocPreview',
    components: { MailDocCanvas },
    props: {
        template: {
            type: String,
            default: '',
        },
        refreshKey: {
            type: [Number, String],
            default: 0,
        },
        debounceMs: {
            type: Number,
            default: 250,
        },
        // 변수 알약에 표시할 실제 값 맵 — MailDocCanvas 로 그대로 전달.
        values: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            rendered: '',
            loading: false,
            error: '',
            debounceTimer: null,
            cache: new Map(),
            renderToken: 0,
        }
    },
    watch: {
        template: {
            immediate: true,
            handler() {
                this.scheduleRender()
            },
        },
        refreshKey() {
            this.cache.clear()
            this.scheduleRender(0)
        },
    },
    beforeDestroy() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
            this.debounceTimer = null
        }
        this.renderToken++
    },
    methods: {
        scheduleRender(delay) {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer)
            }

            // 실행 대기 중에도 이전 비동기 요청 결과를 무효화한다.
            const token = ++this.renderToken
            const ms = delay == null ? this.debounceMs : delay

            this.debounceTimer = setTimeout(() => {
                this.debounceTimer = null
                this.render(token)
            }, ms)
        },
        async render(token) {
            if (token !== this.renderToken) return

            if (!this.template) {
                this.rendered = ''
                this.loading = false
                this.error = ''
                return
            }

            this.loading = true
            this.error = ''

            try {
                const html = await snippetExpand.expandSnippetRefs(
                    this.template,
                    {
                        fetchSnippet: (code, version) => {
                            return this.fetchSnippet(code, version)
                        },
                        // 읽기 전용 메일 미리보기에는 스니펫 경계 장식을 넣지 않는다.
                        renderSnippet: ({ innerHtml }) => innerHtml,
                        renderError: ({ code, message }) => {
                            return this.errorSpan(code, message)
                        },
                    }
                )

                if (token !== this.renderToken) return
                this.rendered = html
            } catch (e) {
                if (token !== this.renderToken) return
                this.error = `미리보기 오류: ${e.message || e}`
            } finally {
                if (token === this.renderToken) {
                    this.loading = false
                }
            }
        },
        async fetchSnippet(code, version) {
            const key = `${code}::${version == null ? '' : version}`
            if (this.cache.has(key)) return this.cache.get(key)

            const url =
                version != null
                    ? `/mail/snippet/${encodeURIComponent(code)}/${version}`
                    : `/mail/snippet/${encodeURIComponent(code)}`
            const response = await this.$axios.get(url)
            const content =
                response.data &&
                (response.data.content || response.data.body || '')

            this.cache.set(key, content || '')
            return content || ''
        },
        errorSpan(code, message) {
            const label = code || '잘못된 구문'
            return (
                `<span class="mail-snippet-error" title="${escapeHtml(message)}">` +
                `[스니펫 오류: ${escapeHtml(label)}]` +
                '</span>'
            )
        },
    },
}
</script>

<style scoped>
.mail-doc-preview {
    position: relative;
    width: 100%;
    min-height: 320px;
    background: #fff;
}

.mail-doc-preview__status {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
    display: flex;
    align-items: center;
    padding: 4px 8px;
    color: #6c757d;
    font-size: 12px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid #dee2e6;
    border-radius: 4px;
    pointer-events: none;
}

.mail-doc-preview__error {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 2;
    max-width: calc(100% - 120px);
    padding: 4px 8px;
    color: #721c24;
    font-size: 12px;
    background: rgba(248, 215, 218, 0.95);
    border: 1px solid #f5c6cb;
    border-radius: 4px;
}

.mail-doc-preview__empty {
    min-height: 320px;
    padding: 24px;
    text-align: center;
    background: #fff;
}

.mail-doc-preview__canvas {
    width: 100%;
}
</style>
